/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { Cookies } from "react-cookie";

import axios from "connection";

import { fetchConfigFromAPI, setLoader, setModal } from "state/config-slice";
import CartSlice, { CartSliceFromRoot } from "types/CartSlice";
import CashbackRules, { CashbackRule } from "types/CashbackRules";

import Offer, { Status } from "types/Offer";

import { setDebugTransactions } from "utils/debugHelper";

import { API_URL, PPE_PATH } from "config";
import { setNeedsRefetching } from "state/offers-slice";

const initialState = {
  cart: [],
  cartPurchaseCount: 0,
  numberOfSubscribedOffers: 0,
  cashbackPrice: 0,
  cashbackDiscount: 0,
  chosenDiscount: 0,
  cashbackRules: [
    {
      partnersMin: 1,
      partnersMax: 1,
      cashback: 0.05,
    },
    {
      partnersMin: 2,
      cashback: 0.1,
    },
  ],
} as CartSlice;

export const purchaseOffersInCart = createAsyncThunk(
  "cart/purchaseOffersInCart",
  async (_arg, { getState, dispatch }: any) => {
    const offerPackageIDsInCart: any[] = getState().cart.cart.map((el: Offer) => ({ offerName: el.name }));

    try {
      const result = await axios.post(`${API_URL}${PPE_PATH}/offers`, offerPackageIDsInCart);

      if (result.status === 200) {
        if (result.data.purchased.length > 0) {
          result.data.purchased.map((offer: { offerName: string }) =>
            dispatch(removeOfferFromCart({ name: offer.offerName })),
          );
        }

        dispatch(setNeedsRefetching(true));
        dispatch(clearCart());
      }

      setDebugTransactions(result);
      return result.data;
    } catch (error) {
      dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));

      dispatch(
        setModal({
          showModal: true,
          errorModal: true,
        }),
      );

      return Promise.reject(error);
    }
  },
);

export const expressPurchaseSubscriptions = createAsyncThunk(
  "/expressPurchaseSubscriptions",
  async (paymentCardId: undefined | string = undefined, { getState, dispatch }: any) => {
    const offerNamesInCart: any[] = getState().cart.cart.map((el: Offer) => ({
      offerName: el.name,
      packageId: el.longPackageId,
      shortPackageId: el.packageId,
      partner: el?.partner,
      type: el?.type,
      userGroup: el?.usergroup,
      trial: el?.trial,
      userData: el?.userData,
    }));

    try {
      const result = await axios.post(`${API_URL}${PPE_PATH}/offers/expressPurchase`, {
        purchaseOfferList: offerNamesInCart,
        paymentCardId,
      });

      dispatch(clearCart());

      setDebugTransactions(result);

      return result.data;
    } catch (error) {
      dispatch(setLoader({ showLoader: false }));
      dispatch(
        setModal({
          showModal: true,
          errorModal: true,
        }),
      );

      return Promise.reject(error);
    }
  },
);

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCartOffers: (state, action) => {
      const offers = (action.payload.offers || []) as Offer[];

      const newOffers = offers.filter(
        (offer: Offer) => offer.marketplaceInfo?.status === Status.purchasable,
      ) as Offer[];

      const newOfferNames = newOffers.map((offer) => offer.name);

      const existingOffers = state.cart.filter((offer) => !newOfferNames.includes(offer.name));
      const newCart = [...existingOffers, ...newOffers] as Offer[];

      return {
        ...state,
        cart: newCart,
      };
    },
    addOfferToCart: (state, action) => {
      if (action.payload.marketplaceInfo?.status !== Status.purchasable) {
        return {
          ...state,
        };
      }

      const newCart = [
        ...state.cart.filter((offer) => offer.name !== action.payload.name),
        { ...action.payload },
      ] as Offer[];

      const cookies = new Cookies();

      cookies.set("CART", JSON.stringify(newCart.map((offer) => offer.name)), { path: "/" });

      return {
        ...state,
        cart: newCart,
      };
    },
    removeOfferFromCart: (state, action) => {
      const newCart = [...state.cart.filter((el) => el.name !== action.payload.name)] as Offer[];

      const cookies = new Cookies();

      cookies.set("CART", JSON.stringify(newCart.map((offer) => offer.name)), { path: "/" });

      return {
        ...state,
        cart: newCart,
      };
    },
    clearCart: (state) => {
      const cookies = new Cookies();

      cookies.remove("CART", { path: "/" });

      return {
        ...state,
        cart: [],
      };
    },
    clearCartState: () => {
      const cookies = new Cookies();

      cookies.remove("CART", { path: "/" });

      return {
        ...initialState,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchConfigFromAPI.fulfilled, (state, action) => {
      if (action.payload.cashbackConfiguration) {
        state.cashbackRules = [
          ...action.payload.cashbackConfiguration.sort(
            (a: CashbackRule, b: CashbackRule) => a.partnersMin - b.partnersMin,
          ),
        ];
      }

      state.cashbackRules = initialState.cashbackRules;
    }),
      builder.addCase(purchaseOffersInCart.fulfilled, (state) => {
        state.cartPurchaseCount = state.cartPurchaseCount + 1;
      });
  },
});

export const { setCartOffers, addOfferToCart, removeOfferFromCart, clearCart, clearCartState } = cartSlice.actions;

export const cartState = createSelector(
  (state) => state.cart || {},
  (cart) => cart as CartSlice,
);

export const cashbackRulesLoaded = (state: CartSliceFromRoot): CashbackRules =>
  state.cart.cashbackRules || (initialState.cashbackRules as CashbackRules);

export const agencyOffers = createSelector(cartState, ({ cart }) =>
  cart.filter(({ marketplaceInfo }) => marketplaceInfo.agency === true),
);

export const resellerOffers = createSelector(cartState, ({ cart }) =>
  cart.filter(({ marketplaceInfo }) => marketplaceInfo.agency === false || marketplaceInfo.agency === undefined),
);

export const resellerOffersWithTrial = createSelector(cartState, ({ cart }) =>
  cart.filter(({ marketplaceInfo, trial }) => marketplaceInfo.agency === false && trial === true),
);

export const freeTrialOffers = createSelector(cartState, ({ cart }) => cart.filter(({ trial }) => trial === true));

export const checkIfOfferIsInCart =
  (name: string | undefined): any =>
  (state: CartSliceFromRoot): any => {
    return state?.cart?.cart.find((el) => el.name === name) !== undefined;
  };

export const checkIsSwitchableOffersByPartnerInCart =
  (partner: string | undefined): any =>
  (state: CartSliceFromRoot): any => {
    return (
      state?.cart?.cart.find(
        (el) => el.partner === partner && (el.switchable === true || el.multiPlanOffer === true),
      ) !== undefined
    );
  };

export const isOfferFromPartnerAlreadyInCart =
  (partner: string | undefined): any =>
  (state: CartSliceFromRoot): any => {
    return state?.cart?.cart.find((el) => el.partner === partner) !== undefined;
  };

export default cartSlice.reducer;
